import React, { useRef, useState } from "react";
import {
  Select,
  Radio,
  Checkbox,
  Upload,
  Input,
  Button,
  Card,
  Collapse,
  Space,
  Typography,
  Row,
  Col,
  Image,
  Tag,
  Modal,
  Divider,
} from "antd";
import {
  FileImageOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { getImageUrl } from "../../utils/imageUtils";
import { getCurrencySymbol } from "../../utils/currencyUtils";
import { cloneCustomSections } from "../../utils/pdfOptionsPersistence";

const { Option } = Select;

/** Format price for custom section title: integer with space thousands separator + symbol (e.g. "1 234 Kč") */
function formatPriceForSectionTitle(price, currencyCode) {
  const num = Number(price);
  if (!Number.isFinite(num) || num <= 0) return null;
  const symbol = getCurrencySymbol(currencyCode || "CZK");
  const formatted = String(Math.round(num)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${formatted} ${symbol}`;
}

/** Custom section layout options: value, label, max images */
const CUSTOM_SECTION_LAYOUTS = [
  { value: "1", label: "1 image", maxImages: 1 },
  { value: "2_1c", label: "2 images (1 column)", maxImages: 2 },
  { value: "4_2x2", label: "4 images (2×2)", maxImages: 4 },
  { value: "6_3x2", label: "6 images (2×3)", maxImages: 6 },
];

export function getMaxImagesForLayout(layout) {
  const found = CUSTOM_SECTION_LAYOUTS.find((l) => l.value === layout);
  return found ? found.maxImages : 1;
}

/** Mini grid preview for layout: 1, 2x1, 2x2, 2x3 */
function LayoutPreview({ value }) {
  const gap = 2;
  const cell = 10;
  const map = {
    "1": { cols: 1, rows: 1 },
    "2_1c": { cols: 1, rows: 2 },
    "4_2x2": { cols: 2, rows: 2 },
    "6_3x2": { cols: 2, rows: 3 },
  };
  const { cols, rows } = map[value] || map["1"];
  const w = cols * cell + (cols - 1) * gap;
  const h = rows * cell + (rows - 1) * gap;
  return (
    <div
      style={{
        display: "inline-flex",
        flexWrap: "wrap",
        width: w,
        height: h,
        gap,
        alignContent: "flex-start",
        alignItems: "flex-start",
      }}
    >
      {Array.from({ length: cols * rows }, (_, i) => (
        <div
          key={i}
          style={{
            width: cell,
            height: cell,
            borderRadius: 2,
            backgroundColor: "#1890ff",
            opacity: 0.85,
          }}
        />
      ))}
    </div>
  );
}

/** Fixed size for all layout tiles */
const LAYOUT_TILE_WIDTH = 88;
const LAYOUT_TILE_HEIGHT = 90;
const LAYOUT_PREVIEW_SIZE = 44;

/** Single layout tile (radio-style), same size for all */
function LayoutTile({ layout, selected, onSelect }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(layout.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(layout.value);
        }
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        width: LAYOUT_TILE_WIDTH,
        height: LAYOUT_TILE_HEIGHT,
        padding: 8,
        border: `2px solid ${selected ? "#1890ff" : "#d9d9d9"}`,
        borderRadius: 8,
        background: selected ? "#e6f4ff" : "#fafafa",
        cursor: "pointer",
        transition: "border-color 0.2s, background 0.2s",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: LAYOUT_PREVIEW_SIZE,
          height: LAYOUT_PREVIEW_SIZE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LayoutPreview value={layout.value} />
      </div>
      <span style={{ fontSize: 11, color: "#262626", textAlign: "center" }}>
        {layout.label}
      </span>
    </div>
  );
}

const { Panel } = Collapse;
const { TextArea } = Input;
const { Text } = Typography;

const PDFTemplateOptions = ({
  selectedTemplate,
  setSelectedTemplate,
  templateOptions,
  pdfOptions,
  setPdfOptions,
  proposal,
  uploadingImages,
  handleImageUpload,
  handleCustomSectionImageUpload,
  uploadingCustomSectionImages,
  draggedImage,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  editingImageId,
  editingDescription,
  setEditingImageId,
  setEditingDescription,
  handleImageDescriptionUpdate,
  handleImageDelete,
  handleDownloadPDF,
}) => {
  const [activePanels, setActivePanels] = useState([]);
  const processedUploadUidsRef = useRef(new Set());
  const pendingCustomSectionFilesRef = useRef({});
  const customSectionFlushTimersRef = useRef({});

  const handleCustomSectionUploadChange = (sectionIndex, info) => {
    const { file } = info;
    if (!file || file.status === "removed") return;

    const dedupeKey = `cs:${sectionIndex}:${file.uid}`;
    if (processedUploadUidsRef.current.has(dedupeKey)) return;
    processedUploadUidsRef.current.add(dedupeKey);

    const rawFile = file.originFileObj ?? file;
    if (!(rawFile instanceof File || rawFile instanceof Blob)) return;

    const pending = pendingCustomSectionFilesRef.current;
    if (!pending[sectionIndex]) {
      pending[sectionIndex] = [];
    }
    pending[sectionIndex].push(rawFile);

    // Ant Design fires onChange once per file in a multi-select. Batch them
    // so the handler can cap against remaining layout slots in one pass.
    clearTimeout(customSectionFlushTimersRef.current[sectionIndex]);
    customSectionFlushTimersRef.current[sectionIndex] = setTimeout(() => {
      const files = pending[sectionIndex] || [];
      pending[sectionIndex] = [];
      if (files.length) {
        handleCustomSectionImageUpload(sectionIndex, files);
      }
    }, 0);
  };

  const handleUploadChange = (itemId, info) => {
    const { file } = info;
    if (!file || file.status === "removed") return;

    const dedupeKey = `${itemId}:${file.uid}`;
    if (processedUploadUidsRef.current.has(dedupeKey)) return;
    processedUploadUidsRef.current.add(dedupeKey);

    const originFile = file.originFileObj;
    if (originFile instanceof File) {
      handleImageUpload(itemId, [originFile]);
    }
  };

  // Custom upload props
  const uploadProps = (itemId) => ({
    multiple: true,
    accept: "image/*",
    beforeUpload: () => false, // Prevent auto upload
    onChange: (info) => handleUploadChange(itemId, info),
    disabled: uploadingImages[itemId],
  });

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      {/* Template Selection - Always Visible */}
      <Text strong style={{ display: "block", marginBottom: 8 }}>
        Select Template
      </Text>
      <Select
        value={selectedTemplate}
        onChange={setSelectedTemplate}
        style={{ width: "100%" }}
        size="large"
      >
        {templateOptions.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>

      {selectedTemplate === "product_summary" && (
        <Card size="small">
          <Space orientation="vertical" size="small" style={{ width: "100%" }}>
            <Checkbox
              checked={!!pdfOptions.hidePriceSummary}
              onChange={(e) =>
                setPdfOptions((opts) => ({
                  ...opts,
                  hidePriceSummary: e.target.checked,
                }))
              }
            >
              Hide price summary (subtotal / DPH / total)
            </Checkbox>
            <Checkbox
              checked={!!pdfOptions.showLineTotalWithVat}
              onChange={(e) =>
                setPdfOptions((opts) => ({
                  ...opts,
                  showLineTotalWithVat: e.target.checked,
                }))
              }
            >
              Show line total with VAT (Cena s DPH celkem for quantity)
            </Checkbox>
            <Checkbox
              checked={!!pdfOptions.showDiscountPercentage}
              onChange={(e) =>
                setPdfOptions((opts) => ({
                  ...opts,
                  showDiscountPercentage: e.target.checked,
                }))
              }
            >
              Show discount percentage on discounted items
            </Checkbox>
            <Checkbox
              checked={!!pdfOptions.stripVat}
              onChange={(e) =>
                setPdfOptions((opts) => ({
                  ...opts,
                  stripVat: e.target.checked,
                }))
              }
            >
              Odečíst DPH (show prices without VAT)
            </Checkbox>
          </Space>
        </Card>
      )}

      {/* Product Customization - Accordion */}
      <Divider orientation="left" size="small">
        Products
      </Divider>
      {selectedTemplate === "product_summary" &&
        proposal.items &&
        proposal.items.length > 0 && (
          <Collapse
            activeKey={activePanels}
            onChange={setActivePanels}
            style={{ width: "100%" }}
          >
            {proposal.items.map((item) => (
              <Panel
                header={
                  <Space>
                    <FileImageOutlined />
                    <Text strong>{item.item_name}</Text>
                    {pdfOptions.selectedProductSummaryImages?.[
                      item.proposal_item_id
                    ] && <Tag color="blue">Image Selected</Tag>}
                  </Space>
                }
                key={item.proposal_item_id}
              >
                <Space
                  orientation="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  {/* Image Selection */}
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 12 }}>
                      Select Image for PDF (Choose 1)
                    </Text>
                    <Radio.Group
                      value={
                        pdfOptions.selectedProductSummaryImages?.[
                          item.proposal_item_id
                        ]
                      }
                      onChange={(e) => {
                        setPdfOptions((opts) => ({
                          ...opts,
                          selectedProductSummaryImages: {
                            ...opts.selectedProductSummaryImages,
                            [item.proposal_item_id]: e.target.value,
                          },
                        }));
                      }}
                      style={{ width: "100%" }}
                    >
                      <Row gutter={[16, 16]}>
                        {(item.all_images || []).map((image) => (
                          <Col xs={12} sm={8} md={6} key={image.image_id}>
                            <Card
                              hoverable
                              style={{
                                border:
                                  pdfOptions.selectedProductSummaryImages?.[
                                    item.proposal_item_id
                                  ] === image.image_id
                                    ? "2px solid #1890ff"
                                    : "1px solid #d9d9d9",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setPdfOptions((opts) => ({
                                  ...opts,
                                  selectedProductSummaryImages: {
                                    ...opts.selectedProductSummaryImages,
                                    [item.proposal_item_id]: image.image_id,
                                  },
                                }));
                              }}
                            >
                              <div
                                style={{ textAlign: "center", marginBottom: 8 }}
                              >
                                <Radio value={image.image_id}>
                                  <Tag
                                    color={
                                      image.is_uploaded
                                        ? "green"
                                        : image.is_primary
                                        ? "blue"
                                        : "default"
                                    }
                                  >
                                    {image.is_uploaded
                                      ? "Uploaded"
                                      : image.is_primary
                                      ? "Primary"
                                      : "Product"}
                                  </Tag>
                                </Radio>
                              </div>
                              <Image
                                src={getImageUrl(image.image_url)}
                                alt="Product"
                                style={{
                                  width: "100%",
                                  height: "150px",
                                  objectFit: "cover",
                                }}
                                preview={false}
                              />
                              <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {image.description || "No description"}
                                </Text>
                              </div>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Radio.Group>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Upload Additional Images
                    </Text>
                    <Upload {...uploadProps(item.proposal_item_id)}>
                      <Button
                        icon={<UploadOutlined />}
                        disabled={uploadingImages[item.proposal_item_id]}
                        loading={uploadingImages[item.proposal_item_id]}
                      >
                        {uploadingImages[item.proposal_item_id]
                          ? "Uploading..."
                          : "Upload Images"}
                      </Button>
                    </Upload>
                  </div>

                  {/* Custom Description */}
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Custom Description for PDF
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 12,
                          fontWeight: "normal",
                          marginLeft: 8,
                        }}
                      >
                        (Optional)
                      </Text>
                    </Text>
                    <TextArea
                      rows={3}
                      placeholder="Enter a custom description for this product in the PDF (optional)..."
                      value={
                        pdfOptions.customDescriptions?.[
                          `item_${item.proposal_item_id}`
                        ] ||
                        item.custom_description ||
                        ""
                      }
                      onChange={(e) =>
                        setPdfOptions((opts) => ({
                          ...opts,
                          customDescriptions: {
                            ...opts.customDescriptions,
                            [`item_${item.proposal_item_id}`]: e.target.value,
                          },
                        }))
                      }
                    />
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block", marginTop: 4 }}
                    >
                      This description will appear in the PDF for this specific
                      product. Leave empty to use the default description or
                      hide it.
                    </Text>
                  </div>
                </Space>
              </Panel>
            ))}
          </Collapse>
        )}

      {/* Additional Information & Custom Sections - Accordion */}
      {selectedTemplate === "product_summary" && (
        <Collapse defaultActiveKey={[]}>
          {/* Additional Information */}
          <Panel
            header={
              <Space>
                <FileTextOutlined />
                <Text strong>Additional Information</Text>
                {pdfOptions.additionalInformation && (
                  <Tag color="blue">Configured</Tag>
                )}
              </Space>
            }
            key="additional-info"
          >
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Additional Information for PDF
                </Text>
                <TextArea
                  rows={4}
                  placeholder="Enter additional information that will appear in the 'Doplňující informace' section of the PDF..."
                  value={pdfOptions.additionalInformation || ""}
                  onChange={(e) =>
                    setPdfOptions((opts) => ({
                      ...opts,
                      additionalInformation: e.target.value,
                    }))
                  }
                />
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginTop: 4 }}
                >
                  This text will appear in the "Doplňující informace" section.
                  Leave empty to hide the section.
                </Text>
              </div>
            </Space>
          </Panel>

          {/* Custom Sections */}
          <Panel
            header={
              <Space>
                <PlusOutlined />
                <Text strong>Custom Sections</Text>
                {(pdfOptions.customSections || []).length > 0 && (
                  <Tag color="blue">
                    {(pdfOptions.customSections || []).length} section(s)
                  </Tag>
                )}
              </Space>
            }
            key="custom-sections"
            extra={
              <Space size="small" onClick={(e) => e.stopPropagation()}>
                {selectedTemplate === "product_summary" &&
                  proposal?.items?.length > 0 && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        const currencyCode = proposal.currency_code || "CZK";
                        const newSections = proposal.items.map((item, index) => {
                          const title =
                            formatPriceForSectionTitle(
                              item.final_price,
                              currencyCode
                            ) != null
                              ? `${item.item_name || ""} - ${formatPriceForSectionTitle(item.final_price, currencyCode)}`
                              : (item.item_name || "").trim() || "Untitled";
                          return {
                            id: `from_item_${item.proposal_item_id ?? index}_${Date.now()}`,
                            title,
                            images: [],
                            layout: "1",
                          };
                        });
                        setPdfOptions((opts) => ({
                          ...opts,
                          customSections: newSections,
                        }));
                      }}
                    >
                      Create from proposal items
                    </Button>
                  )}
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    const newSection = {
                      id: `cs_${Date.now()}`,
                      title: "",
                      images: [],
                      layout: "1",
                    };
                    setPdfOptions((opts) => ({
                      ...opts,
                      customSections: [
                        ...cloneCustomSections(opts.customSections),
                        newSection,
                      ],
                    }));
                  }}
                >
                  Add Section
                </Button>
              </Space>
            }
          >
            <Space orientation="vertical" style={{ width: "100%" }} size="large">
              {(pdfOptions.customSections || []).map(
                (section, sectionIndex) => (
                  <Card
                    key={section.id ?? `cs-${sectionIndex}`}
                    title={
                      <Input
                        placeholder="Section title"
                        value={section.title}
                        onChange={(e) => {
                          const updatedSections = cloneCustomSections(
                            pdfOptions.customSections
                          );
                          updatedSections[sectionIndex].title = e.target.value;
                          setPdfOptions((opts) => ({
                            ...opts,
                            customSections: updatedSections,
                          }));
                        }}
                        style={{ width: 300 }}
                      />
                    }
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          const updatedSections = cloneCustomSections(
                            pdfOptions.customSections
                          ).filter((_, index) => index !== sectionIndex);
                          setPdfOptions((opts) => ({
                            ...opts,
                            customSections: updatedSections,
                          }));
                        }}
                      >
                        Remove
                      </Button>
                    }
                  >
                    <Space
                      orientation="vertical"
                      style={{ width: "100%" }}
                      size="middle"
                    >
                      <Space align="center" wrap size="middle">
                        <Text strong style={{ fontSize: 12 }}>
                          Layout
                        </Text>
                        <Row gutter={[8, 8]}>
                          {CUSTOM_SECTION_LAYOUTS.map((layout) => (
                            <Col key={layout.value}>
                              <LayoutTile
                                layout={layout}
                                selected={
                                  (section.layout ?? "2_1c") === layout.value
                                }
                                onSelect={(newLayout) => {
                                  const maxNew =
                                    getMaxImagesForLayout(newLayout);
                                  const updatedSections = cloneCustomSections(
                                    pdfOptions.customSections
                                  );
                                  updatedSections[sectionIndex].layout =
                                    newLayout;
                                  if (
                                    (updatedSections[sectionIndex].images
                                      ?.length || 0) > maxNew
                                  ) {
                                    updatedSections[sectionIndex].images =
                                      updatedSections[
                                        sectionIndex
                                      ].images.slice(0, maxNew);
                                  }
                                  setPdfOptions((opts) => ({
                                    ...opts,
                                    customSections: updatedSections,
                                  }));
                                }}
                              />
                            </Col>
                          ))}
                        </Row>
                      </Space>
                      <Text strong style={{ fontSize: 12 }}>
                        Images (Max{" "}
                        {getMaxImagesForLayout(section.layout ?? "2_1c")})
                      </Text>
                      <Row gutter={[16, 16]}>
                        {(section.images || [])
                          .slice(
                            0,
                            getMaxImagesForLayout(section.layout ?? "2_1c")
                          )
                          .map((image, imageIndex) => (
                          <Col
                            xs={24}
                            sm={12}
                            key={`${section.id ?? sectionIndex}-${image.url}-${imageIndex}`}
                          >
                            <Card size="small">
                              <Image
                                src={
                                  image.url.startsWith("blob:")
                                    ? image.url
                                    : getImageUrl(image.url)
                                }
                                alt="Custom section"
                                style={{
                                  maxHeight: "100px",
                                  objectFit: "cover",
                                  width: "100%",
                                }}
                                preview={false}
                              />
                              <Input
                                placeholder="Image description (optional)"
                                value={image.description || ""}
                                onChange={(e) => {
                                  const updatedSections = cloneCustomSections(
                                    pdfOptions.customSections
                                  );
                                  updatedSections[sectionIndex].images[
                                    imageIndex
                                  ].description = e.target.value;
                                  setPdfOptions((opts) => ({
                                    ...opts,
                                    customSections: updatedSections,
                                  }));
                                }}
                                style={{ marginTop: 8 }}
                              />
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  const updatedSections = cloneCustomSections(
                                    pdfOptions.customSections
                                  );
                                  updatedSections[sectionIndex].images.splice(
                                    imageIndex,
                                    1
                                  );
                                  setPdfOptions((opts) => ({
                                    ...opts,
                                    customSections: updatedSections,
                                  }));
                                }}
                                style={{ width: "100%", marginTop: 8 }}
                              >
                                Remove Image
                              </Button>
                            </Card>
                          </Col>
                        ))}
                      </Row>

                      {(() => {
                        const remainingSlots =
                          getMaxImagesForLayout(section.layout ?? "2_1c") -
                          (section.images?.length ?? 0);
                        if (remainingSlots <= 0) return null;
                        return (
                          <Upload
                            accept="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                            multiple={remainingSlots > 1}
                            maxCount={remainingSlots}
                            fileList={[]}
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={(info) =>
                              handleCustomSectionUploadChange(
                                sectionIndex,
                                info
                              )
                            }
                            disabled={
                              uploadingCustomSectionImages[sectionIndex]
                            }
                          >
                            <Button
                              icon={<UploadOutlined />}
                              loading={
                                uploadingCustomSectionImages[sectionIndex]
                              }
                              disabled={
                                uploadingCustomSectionImages[sectionIndex]
                              }
                            >
                              {uploadingCustomSectionImages[sectionIndex]
                                ? "Uploading..."
                                : remainingSlots > 1
                                  ? `Upload Images (up to ${remainingSlots})`
                                  : "Upload Image"}
                            </Button>
                          </Upload>
                        );
                      })()}
                    </Space>
                  </Card>
                )
              )}
              {(pdfOptions.customSections || []).length === 0 && (
                <div style={{ textAlign: "center", padding: "24px" }}>
                  <Text type="secondary">
                    No custom sections added. Click "Add Section" above to
                    create one.
                  </Text>
                </div>
              )}
            </Space>
          </Panel>
        </Collapse>
      )}

      {selectedTemplate === "image_proposal" && (
        <Card>
          <Checkbox
            checked={pdfOptions.showCustomerInfo}
            onChange={(e) =>
              setPdfOptions((opts) => ({
                ...opts,
                showCustomerInfo: e.target.checked,
              }))
            }
          >
            Show Customer Info
          </Checkbox>
        </Card>
      )}

      {selectedTemplate === "upgraded_image_proposal" && (
        <Card>
          <Checkbox
            checked={pdfOptions.showCustomerInfo}
            onChange={(e) =>
              setPdfOptions((opts) => ({
                ...opts,
                showCustomerInfo: e.target.checked,
              }))
            }
          >
            Show Customer Info
          </Checkbox>
        </Card>
      )}

      {/* Image Management Section - Only for Image Proposal */}
      {(selectedTemplate === "image_proposal" ||
        selectedTemplate === "upgraded_image_proposal") && (
        <>
          {proposal.items?.map((item) => (
            <Card
              key={item.proposal_item_id}
              title={<Text strong>{item.item_name}</Text>}
              style={{ marginBottom: 16 }}
            >
              <Space
                orientation="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                {/* Image Selection Section */}
                <div>
                  <Text strong style={{ display: "block", marginBottom: 12 }}>
                    Select Images for PDF:
                  </Text>
                  <Row gutter={[16, 16]}>
                    {(item.all_images || []).map((image) => (
                      <Col xs={12} sm={8} md={6} key={image.image_id}>
                        <Card
                          hoverable
                          style={{
                            border:
                              Array.isArray(
                                pdfOptions.selectedImages?.[
                                  item.proposal_item_id
                                ]
                              ) &&
                              pdfOptions.selectedImages[
                                item.proposal_item_id
                              ].includes(image.image_id)
                                ? "2px solid #1890ff"
                                : "1px solid #d9d9d9",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const currentSelected =
                              pdfOptions.selectedImages?.[
                                item.proposal_item_id
                              ] || [];
                            const isSelected = currentSelected.includes(
                              image.image_id
                            );
                            const newSelected = isSelected
                              ? currentSelected.filter(
                                  (id) => id !== image.image_id
                                )
                              : [...currentSelected, image.image_id];

                            setPdfOptions((opts) => ({
                              ...opts,
                              selectedImages: {
                                ...opts.selectedImages,
                                [item.proposal_item_id]: newSelected,
                              },
                            }));
                          }}
                        >
                          <div style={{ textAlign: "center", marginBottom: 8 }}>
                            <Checkbox
                              checked={
                                Array.isArray(
                                  pdfOptions.selectedImages?.[
                                    item.proposal_item_id
                                  ]
                                )
                                  ? pdfOptions.selectedImages[
                                      item.proposal_item_id
                                    ].includes(image.image_id)
                                  : false
                              }
                              onChange={(e) => {
                                e.stopPropagation();
                                const currentSelected =
                                  pdfOptions.selectedImages?.[
                                    item.proposal_item_id
                                  ] || [];
                                const newSelected = e.target.checked
                                  ? [...currentSelected, image.image_id]
                                  : currentSelected.filter(
                                      (id) => id !== image.image_id
                                    );

                                setPdfOptions((opts) => ({
                                  ...opts,
                                  selectedImages: {
                                    ...opts.selectedImages,
                                    [item.proposal_item_id]: newSelected,
                                  },
                                }));
                              }}
                            >
                              <Tag
                                color={
                                  image.is_uploaded
                                    ? "green"
                                    : image.is_primary
                                    ? "blue"
                                    : "default"
                                }
                              >
                                {image.is_uploaded
                                  ? "Uploaded"
                                  : image.is_primary
                                  ? "Primary Product"
                                  : "Product"}
                              </Tag>
                            </Checkbox>
                          </div>
                          <Image
                            src={getImageUrl(image.image_url)}
                            alt="Product"
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                            preview={false}
                          />
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {image.description || "No description"}
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Image Upload */}
                <div>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Upload Additional Images
                  </Text>
                  <Upload {...uploadProps(item.proposal_item_id)}>
                    <Button
                      icon={<UploadOutlined />}
                      disabled={uploadingImages[item.proposal_item_id]}
                      loading={uploadingImages[item.proposal_item_id]}
                    >
                      {uploadingImages[item.proposal_item_id]
                        ? "Uploading..."
                        : "Upload Images"}
                    </Button>
                  </Upload>
                </div>
              </Space>
            </Card>
          ))}
        </>
      )}

      {/* Image Description Editing Modal */}
      <Modal
        title="Edit Image Description"
        open={!!editingImageId}
        onCancel={() => {
          setEditingImageId(null);
          setEditingDescription("");
        }}
        onOk={() => {
          handleImageDescriptionUpdate(editingImageId, editingDescription);
          setEditingImageId(null);
          setEditingDescription("");
        }}
        okText="Save"
        cancelText="Cancel"
      >
        <Input
          value={editingDescription}
          onChange={(e) => setEditingDescription(e.target.value)}
          placeholder="Image description"
        />
      </Modal>

      {/* Generate PDF Button */}
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
      >
        <Button type="primary" size="large" onClick={handleDownloadPDF}>
          Generate PDF
        </Button>
      </div>
    </Space>
  );
};

export default PDFTemplateOptions;
